import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { hours as hrs } from '../lib/hours';
import './HoursApprovalDetail.css';
import RequesterName from './RequesterName';
import { money } from '../lib/money';
import { rowNoRate } from '../lib/contracts';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const day = (v) => (v ? String(v).slice(0, 10) : '—');
const when = (v) => (v ? String(v).replace('T', ' ').slice(0, 16) : '—');

/**
 * The job behind one approval request, for an approver about to sign for it.
 *
 * The queue gives a total, and a total is enough to wave something through and
 * not enough to question it. "12h" does not say whether that was four visits
 * or one implausible afternoon, and the reason it took that long is usually in
 * a comment two pages away that nobody opens. So this shows the sessions one
 * at a time, with their notes, the ticket's own conversation and its history,
 * and lets the decision be made from here rather than from memory.
 *
 * It also checks the arithmetic out loud. The claim was recorded at the moment
 * the contractor submitted, so if the sessions no longer add up to it something
 * moved afterwards - and that is worth saying rather than smoothing over.
 */
export default function HoursApprovalDetail({ request, onClose, onApprove, onRefuse, busy }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refusing, setRefusing] = useState(false);
  const [reason, setReason] = useState('');
  const panel = useRef(null);
  const closer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/approvals/hours/${request.id}/detail`);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not read this job');
    } finally {
      setLoading(false);
    }
  }, [request.id]);

  useEffect(() => { load(); }, [load]);

  // Escape closes, and the focus starts inside the dialogue rather than
  // wherever it happened to be on the page behind it.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    closer.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const t = data?.ticket;
  const r = data?.request;
  const over = r && r.allocated_hours > 0 && r.hours_claimed > r.allocated_hours;

  return (
    <div
      className="had-scrim"
      // A click on the backdrop closes; a click inside must not, which is what
      // the target check is for.
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="had" role="dialog" aria-modal="true"
           aria-label={`${request.ticket_number}: hours awaiting approval`} ref={panel}>
        <div className="had-top">
          <div>
            <span className="had-num">{request.ticket_number}</span>
            <h2>{request.title}</h2>
          </div>
          <button type="button" className="had-x" onClick={onClose} ref={closer}
                  aria-label="Close">&times;</button>
        </div>

        {loading && <p className="had-quiet">Reading the job&hellip;</p>}
        {error && <p className="had-error" role="alert">{error}</p>}

        {data && (
          <>
            <div className="had-figures">
              <div className="had-fig">
                <span className="had-fig-label">Claimed</span>
                <span className={over ? 'had-fig-value is-over' : 'had-fig-value'}>
                  {hrs(r.hours_claimed)}
                </span>
              </div>
              <div className="had-fig">
                <span className="had-fig-label">Budget</span>
                <span className="had-fig-value">
                  {r.allocated_hours > 0 ? hrs(r.allocated_hours) : 'none set'}
                </span>
              </div>
              {r.hourly_rate > 0 && (
                <div className="had-fig">
                  <span className="had-fig-label">At {money(r.hourly_rate)}/h</span>
                  <span className="had-fig-value">{money(r.hours_claimed * r.hourly_rate)}</span>
                </div>
              )}
              <div className="had-fig">
                <span className="had-fig-label">Submitted</span>
                <span className="had-fig-value">{day(r.requested_at)}</span>
              </div>
            </div>

            {rowNoRate(r, t?.contractor_company || t?.contractor_name) && (
              <p className="had-flag is-warn" role="note" data-testid="no-contract-rate">
                {rowNoRate(r, t?.contractor_company || t?.contractor_name)}
              </p>
            )}

            {over && (
              <p className="had-flag">
                {hrs(r.hours_claimed - r.allocated_hours)} over the {hrs(r.allocated_hours)} budgeted
                for this job.
              </p>
            )}

            {/* The claim is a snapshot taken at submission. If it and the
                sessions have since parted company, say so - an approver
                signing for a figure should know it is not the one on file. */}
            {!data.claim_matches_sessions && (
              <p className="had-flag is-warn">
                The sessions below add up to {hrs(data.entries_total)}, but the figure submitted
                for approval was {hrs(r.hours_claimed)}. Signing this off approves{' '}
                {hrs(r.hours_claimed)}.
              </p>
            )}

            <dl className="had-facts">
              <div><dt>Contractor</dt><dd>
                {t.contractor_name || '—'}
                {t.contractor_company ? ` (${t.contractor_company})` : ''}
              </dd></div>
              <div><dt>Raised by</dt><dd><RequesterName ticket={t} fallback="unknown" /></dd></div>
              <div><dt>Status</dt><dd>{t.status || '—'}</dd></div>
              <div><dt>Category</dt><dd>{t.category || '—'}</dd></div>
              <div><dt>Priority</dt><dd>{t.priority || '—'}</dd></div>
              <div><dt>Opened</dt><dd>{day(t.created_at)}</dd></div>
              <div><dt>Finished</dt><dd>{day(t.resolved_at)}</dd></div>
              {t.work_order_number && (
                <div><dt>Work order</dt><dd>{t.work_order_number}</dd></div>
              )}
            </dl>

            {t.description && (
              <section className="had-sec">
                <h3>What was asked for</h3>
                <p className="had-desc">{t.description}</p>
              </section>
            )}

            <section className="had-sec">
              <h3>
                Time logged
                <span className="had-count">
                  {data.entries.length === 0
                    ? 'no sessions'
                    : `${data.entries.length} session${data.entries.length === 1 ? '' : 's'}`
                      + ` · ${hrs(data.entries_total)}`}
                </span>
              </h3>
              {data.entries.length === 0 ? (
                <p className="had-quiet">
                  No individual sessions are recorded against this ticket, which is odd for a
                  job claiming {hrs(r.hours_claimed)} &mdash; worth asking before you sign.
                </p>
              ) : (
                <div className="had-scroll">
                  <table className="had-table">
                    <thead>
                      <tr><th>Date</th><th className="num">Hours</th><th>What was done</th><th>Logged by</th></tr>
                    </thead>
                    <tbody>
                      {data.entries.map((e) => (
                        <tr key={e.id}>
                          <td>{e.date_worked}</td>
                          <td className="num">{hrs(e.hours_logged)}</td>
                          <td>{e.description || <span className="had-quiet">no note</span>}</td>
                          <td>{e.logged_by_contractor || e.logged_by_user || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th>Total</th>
                        <th className="num">{hrs(data.entries_total)}</th>
                        <th colSpan={2} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </section>

            <section className="had-sec">
              <h3>Comments<span className="had-count">{data.comments.length}</span></h3>
              {data.comments.length === 0
                ? <p className="had-quiet">Nothing was written on this ticket.</p>
                : (
                  <ul className="had-feed">
                    {data.comments.map((c) => (
                      <li key={c.id}>
                        <span className="had-who">
                          {c.by_contractor || c.by_user || 'unknown'}
                        </span>
                        <span className="had-at">{when(c.created_at)}</span>
                        <p>{c.comment}</p>
                      </li>
                    ))}
                  </ul>
                )}
            </section>

            <section className="had-sec">
              <h3>History<span className="had-count">{data.history.length}</span></h3>
              {data.history.length === 0
                ? <p className="had-quiet">No recorded changes.</p>
                : (
                  <ul className="had-feed is-tight">
                    {data.history.map((h) => (
                      <li key={h.id}>
                        <span className="had-who">
                          {h.by_contractor || h.by_user || 'system'}
                        </span>
                        <span className="had-at">{when(h.created_at)}</span>
                        <p>
                          {h.status_to
                            ? `${h.status_from || 'unset'} → ${h.status_to}`
                            : 'noted'}
                          {h.reason ? ` — ${h.reason}` : ''}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
            </section>

            {/* Decide from here. Sending the approver back to the row they came
                from to press a second button is how the detail they just read
                gets forgotten. */}
            {r.state === 'pending' ? (
              refusing ? (
                <form
                  className="had-refusal"
                  onSubmit={(e) => { e.preventDefault(); onRefuse(request, reason); }}
                >
                  <label htmlFor="had-why">Why is this going back? The contractor sees this.</label>
                  <textarea id="had-why" rows={3} value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Four hours is too long for a paper jam — please recheck." />
                  <div className="had-actions">
                    <button type="submit" className="had-btn is-danger"
                            disabled={busy || !reason.trim()}>
                      {busy ? 'Sending…' : 'Send back'}
                    </button>
                    <button type="button" className="had-btn"
                            onClick={() => { setRefusing(false); setReason(''); }}>
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="had-actions had-decide">
                  <button type="button" className="had-btn is-primary" disabled={busy}
                          onClick={() => onApprove(request)}>
                    {busy ? 'Approving…' : `Approve ${hrs(r.hours_claimed)}`}
                  </button>
                  <button type="button" className="had-btn" disabled={busy}
                          onClick={() => setRefusing(true)}>
                    Send back
                  </button>
                  <button type="button" className="had-btn is-quiet" onClick={onClose}>
                    Close without deciding
                  </button>
                </div>
              )
            ) : (
              <p className="had-decided">
                Already {r.state}
                {r.decided_by_name ? ` by ${r.decided_by_name}` : ''}
                {r.decided_at ? ` on ${day(r.decided_at)}` : ''}
                {r.reason ? ` — ${r.reason}` : ''}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
